import { i18n } from "i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { FormProvider, useForm as useReactHookForm } from "react-hook-form";
import { I18nextProvider, useTranslation } from "react-i18next";

import { logger } from "c7-logger/index";

import { DesignSystemProvider, LoadingSpinner } from "chameleon/lib/components";
import { ThemePartial } from "chameleon/lib/theme/theme.css";

import {
  fetchingCardTypeAtom,
  lpmPaymentCancelledAtom,
  paymentMessagesAtom,
  paymentSummaryAtom,
  showCardNumberSpinnerAtom,
} from "checkout-experience/atoms/payment-atoms";
import { Header } from "checkout-experience/components/header/header";
import HppContainer from "checkout-experience/components/hpp-container/hpp-container";
import PayInProgressLoader from "checkout-experience/components/pay-in-progress-loader/pay-in-progress-loader";
import PaymentMethod from "checkout-experience/components/payment-method/payment-method";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { PaymentScriptProvider } from "checkout-experience/components/payment-method/payment-script-provider";
import PaymentRequirements from "checkout-experience/components/payment-requirements/payment-requirements";
import { ResetPazePromise } from "checkout-experience/hooks/helpers/initialize-paze";
import { useFormValidation } from "checkout-experience/hooks/use-form-validation";
import { useIsCreditCardWithSurchargeChex } from "checkout-experience/hooks/use-is-credit-card-with-surcharge-chex";
import usePayInterceptor from "checkout-experience/hooks/use-pay-interceptor";
import { useRootStyle } from "checkout-experience/hooks/use-root-style";
import {
  loaderContainer,
  paymentMethodCard,
} from "checkout-experience/layouts/checkout-experience.css";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { loadDeviceFingerprintSDK } from "checkout-experience/utils/load-device-fingerprinting-sdk";
import { mapUiSettingsToTheme } from "checkout-experience/utils/map-ui-settings-to-theme";
import { mapFormErrorsToDatadog } from "checkout-experience/utils/mappers/map-form-errors-to-datadog";
import { PayRequestPayload } from "checkout-query-client/api/types";
import { useFraudServices } from "checkout-query-client/hooks/use-fraud-services";
import { useJWTPayload } from "checkout-query-client/hooks/use-jwt-payload";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  Announcement,
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import {
  ChexDirtyFields,
  ChexFieldValues,
  ChexNestedFormValue,
} from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

import {
  endDatadogSession,
  sendCustomAction,
  sendCustomTiming,
  sendCustomTimingDifference,
  setGlobalContextProperty,
} from "datadog-loader/loader";
import { useSetExperienceGlobalContext } from "datadog-loader/use-set-experience-global-context";

import { isEncryptionFunctioning } from "encrypt-value/encrypt";

import { SendMessage } from "field-frames/src/frame/types";

import {
  GetMerchantSettingsResponse_MerchantSettings,
  IntegrationType,
  PayRequestErrorCode,
  PayResponse,
  PayResponse_Status,
  PlainMessage,
} from "frontend-client";

// @ts-ignore
BigInt.prototype["toJSON"] = function () {
  return this.toString();
};
export interface CheckoutExperienceProps {
  isLoading: boolean;
  makePayment?: (payload: PayRequestPayload) => Promise<PayResponse>;
  onPayResult?: (result: PayResponse) => Promise<PayResponse_Status | void>;
  isPayInProgress: boolean;
  paymentError?: PayRequestErrorCode;
  themeOverrides?: ThemePartial;
  enableDevTools?: string;
  merchantSettings?: PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;
  handleRedirect?: (status: PayResponse_Status) => void;
  kountSessionHostname?: string;
  i18nInstance: i18n;
}

export const CheckoutExperience = ({
  isLoading,
  themeOverrides = undefined,
  isPayInProgress,
  paymentError = undefined,
  makePayment,
  onPayResult,
  handleRedirect,
  kountSessionHostname = "",
  i18nInstance,
}: CheckoutExperienceProps) => {
  const { t, i18n } = useTranslation(["combined"], { i18n: i18nInstance });
  const { showBoundary } = useErrorBoundary();
  const paymentSummary = useAtomValue(paymentSummaryAtom);
  const payByBankRequestId =
    useAtomValue(paymentMessagesAtom)?.payByBankCompletionMessage?.payload
      ?.payResult?.paymentMethod?.payByBank?.payByBankRequestId;
  const lpmPaymentCancelled = useAtomValue(lpmPaymentCancelledAtom);
  const setShowCardSpinner = useSetAtom(showCardNumberSpinnerAtom);
  const setFetchingCardType = useSetAtom(fetchingCardTypeAtom);

  const { publish, subscribe } = usePubSub();
  const {
    merchantSettings,
    experienceType,
    checkoutSessionToken,
    checkoutSession,
    controlledSubmit,
    target,
    deviceFingerprintSDK,
  } = useChexContext();
  const checkoutForm = useReactHookForm<ChexNestedFormValue>({
    mode: "onBlur",
  });
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "" } = paymentOption ?? {};
  const validateForm = useFormValidation(checkoutForm, {
    paymentOption,
    experienceType,
    target,
  });
  useFraudServices(
    merchantSettings?.safetechMerchantId ?? "",
    checkoutSessionToken,
    kountSessionHostname
  );
  const { data: checkoutIntentPayload } = useJWTPayload<{
    mid: string;
    cid: string;
    mor: string;
    ksi: string;
  }>(checkoutSessionToken!);

  const customThemeData = useMemo(
    () =>
      themeOverrides
        ? themeOverrides
        : mapUiSettingsToTheme(merchantSettings?.customTheme),
    [merchantSettings?.customTheme, themeOverrides]
  );

  // If merchant settings country changes, reset the form field to the new country
  // Should only be relevant in the demo site as we should not expect a merchant's country to change
  useEffect(() => {
    if (merchantSettings?.country) {
      checkoutForm.resetField(`${formId}.billingAddressCountry`, {
        defaultValue: merchantSettings?.country,
      });
    }
  }, [merchantSettings?.country]);

  useRootStyle(customThemeData);

  const paymentDomain = usePayInterceptor({
    makePayment,
    onPayResult,
    isPayInProgress,
    handleRedirect,
  });

  const { isFetching: gettingIsCreditCard } =
    useIsCreditCardWithSurchargeChex(checkoutForm);

  // This function waits until there is an encryption key and then uses that to check if encryption is currently allowed and working in the browser.
  // If it isn't it triggers a critical error to end the session.  This only applies to HPP and PBL, since DIU uses the field frames for encryption
  useEffect(() => {
    if (
      merchantSettings?.encryptionKey &&
      (experienceType === "hpp" || experienceType === "hppLink")
    ) {
      void isEncryptionFunctioning(merchantSettings.encryptionKey).then(
        (encryptionWorks) => {
          if (!encryptionWorks) {
            showBoundary(new Error("Encryption is not functioning correctly"));
          }
        }
      );
    }
  }, [merchantSettings, experienceType]);

  // NOTE: We're intentionally passing down this value and setter to keep things on the same DOM level so that the Apple SDK stops yelling bloody murder when it's in different parts of the DOM tree.
  const [applePayComponent, setApplePayComponent] =
    useState<JSX.Element | null>(null);
  const handleSetApplePayComponent = (component: JSX.Element | null) => {
    setApplePayComponent(component);
  };
  const [showPlaceOrderSection, setShowPlaceOrderSection] = useState(true);

  const payOnValidateSubmit = ({
    isPayInProgress,
    isValid,
    orderDigest,
  }: {
    isPayInProgress: boolean;
    isValid: boolean;
    orderDigest?: string;
  }) => {
    const { formState, getValues } = checkoutForm;
    if (!isPayInProgress && isValid) {
      const { formId, paymentType } = paymentOption ?? {};
      const formFields = getValues(`${formId}`) as ChexFieldValues;
      const dirtyFields = formState.dirtyFields[`${formId}`] as ChexDirtyFields;
      paymentDomain({
        merchantSettings,
        formFields,
        dirtyFields,
        submitType:
          paymentType?.toString() === formId
            ? (paymentType ?? PaymentType.creditCard)
            : "storedPaymentMethod",
        paymentMethodId: formId,
        paymentType: paymentType ?? PaymentType.creditCard,
        checkoutSessionData: checkoutSession,
        experienceType,
        paymentSummary,
        payByBankRequestId,
        orderDigest,
        lpmPaymentCancelled,
      })
        .then(() => {
          // This won't be needed after feature flag roll out of "monitor-diu-removal"
          if (merchantSettings?.integrationType === IntegrationType.DROP_IN) {
            endDatadogSession(); // Stop monitoring page once complete
          }
        })
        .catch(logger.error);
    } else if (!isPayInProgress && !isValid) {
      if (!isValid) {
        publish({
          level: "info",
          message: PubSubMessage.PaymentValidationFailed,
          namespace: PubSubNamespace.payment,
        });
        const errors = mapFormErrorsToDatadog(formState, formId);
        sendCustomAction({
          name: PubSubMessage.PaymentValidationFailed.toString(),
          context: {
            formId,
            errors,
          },
        });
      }
    }
  };

  const messageHandler = (value: Announcement) => {
    if (
      value.namespace === PubSubNamespace.external &&
      value.message === PubSubMessage.ExternalSubmitTriggered &&
      value.level === "command"
    ) {
      const globalVerify = new CustomEvent<SendMessage>("action", {
        detail: { message: "validate", payload: {} },
      });
      window.dispatchEvent(globalVerify);
      validateForm(checkoutForm)
        .then((isValid: boolean) => {
          payOnValidateSubmit({
            isValid,
            isPayInProgress,
            orderDigest: value.payload?.orderDigest,
          });
        })
        .catch((err) => {
          logger.error("messageHandler err", err);
        });
    }
  };
  const onSubmit = async () => {
    const isValid = await validateForm();
    if (gettingIsCreditCard) {
      if (experienceType === "dropInUI") {
        setFetchingCardType(true);
      }
      setShowCardSpinner(true);
    } else if (isValid) {
      payOnValidateSubmit({ isValid, isPayInProgress });
    }
  };

  const getPaymentBlock = () => {
    /** This div should not have `card` class because adding gap to messes up spacing  */
    return (
      <div className={paymentMethodCard}>
        <PaymentMethod
          isPayInProgress={isPayInProgress}
          paymentError={paymentError}
          onSubmit={onSubmit}
          setShowPlaceOrderSection={setShowPlaceOrderSection}
          // Apple Pay related Props
          paymentDomain={paymentDomain}
          applePayComponent={applePayComponent}
          setApplePayComponent={handleSetApplePayComponent}
        />
      </div>
    );
  };

  useEffect(() => {
    usePubSub()?.publish({
      level: "info",
      namespace: PubSubNamespace.render,
      message: PubSubMessage.MountSuccess,
    });
  }, []);

  // Clear Paze promise singleton on reload of ChEx.  This is due to some state issue re-rendering
  // the use-enabled-payment-methods hook
  useEffect(() => {
    ResetPazePromise();
  }, []);

  useEffect(() => {
    // This useEffect script is for shield fraud detection
    // https://confluence.prod.aws.jpmchase.net/confluence/pages/viewpage.action?pageId=4478654211
    // https://bitbucketdc.jpmchase.net/projects/RIDER/repos/device-fingerprint-web-sdk
    if (deviceFingerprintSDK) {
      loadDeviceFingerprintSDK(target, deviceFingerprintSDK).catch(() => {
        logger.error("Error loading DFP SDK");
      });
    }
  }, [deviceFingerprintSDK]);

  // check experience type and i18n instance and set document title
  useEffect(() => {
    if (i18n && experienceType !== "dropInUI") {
      document.title = t("htmlTitle");
    }
  }, [i18nInstance, experienceType]);

  useEffect(() => {
    if (!isLoading) {
      usePubSub()?.publish({
        level: "info",
        namespace: PubSubNamespace.render,
        message: PubSubMessage.InitialLoadComplete,
      });
      sendCustomTiming({ name: "chexRenderComplete" });
      sendCustomTimingDifference({
        startName: "chexMountStart",
        endName: "chexRenderCompleteDiff",
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (controlledSubmit && formId) {
      const unsub = subscribe(messageHandler);
      setGlobalContextProperty({ name: "controlledSubmit", context: "true" });
      return () => {
        unsub();
      };
    } else {
      return;
    }
  }, [formId]);

  useSetExperienceGlobalContext(
    experienceType!,
    checkoutIntentPayload!,
    checkoutSession?.currencyCode ?? ""
  );

  return (
    <DesignSystemProvider themeValueOverrides={customThemeData}>
      <I18nextProvider i18n={i18nInstance}>
        <FormProvider {...checkoutForm}>
          <PaymentScriptProvider>
            {isLoading ? (
              <div data-testid="loading-container" className={loaderContainer}>
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <PayInProgressLoader
                  paymentType={paymentSummary?.paymentType}
                  isPayInProgress={isPayInProgress}
                />
                <PaymentRequirements />
                {experienceType === "dropInUI" ? (
                  getPaymentBlock()
                ) : (
                  <>
                    <Header />
                    <HppContainer
                      paymentError={paymentError}
                      showPlaceOrderSection={showPlaceOrderSection}
                      onSubmit={onSubmit}
                      applePayComponent={applePayComponent}
                      isPayInProgress={isPayInProgress}
                      paymentDomain={paymentDomain}
                    >
                      {getPaymentBlock()}
                    </HppContainer>
                  </>
                )}
              </>
            )}
          </PaymentScriptProvider>
        </FormProvider>
      </I18nextProvider>
    </DesignSystemProvider>
  );
};
